import { MOUNT_FR } from './fastRackLabels'

export const RAIL_LENGTHS_MM: Record<number, number> = {
  94: 2387.6,
  139: 3530.6,
  185: 4699.0,
}

export const RAIL_SKUS: Record<number, string> = {
  94: 'FR-UL-RAIL-94',
  139: 'FR-UL-RAIL-139',
  185: 'FR-UL-RAIL-185',
}

export type RailSize = 94 | 139 | 185
export const ALL_RAIL_SIZES: RailSize[] = [185, 139, 94]

const SILVER_TO_BLACK: Record<string, string> = {
  'FR-MIG-CL-1300': 'FR-MIG-CL-B-1301',
  'FR-CL-END-30': 'FR-CL-END-30B',
  'FR-CL-END-32': 'FR-CL-END-32B',
  'FR-CL-END-35': 'FR-CL-END-35B',
  'FR-CL-END-40': 'FR-CL-END-40B',
  'FR-MIGS-30': 'FR-MIGS-30-BL',
  'FR-MIGS-35': 'FR-MIGS-35-BL',
  'FR-MIGS-40': 'FR-MIGS-40-BL',
  'FR-END-30': 'FR-END-30BLK',
  'FR-END-35': 'FR-END-35BLK',
  'FR-END-40': 'FR-END-40BLK',
}

export function resolveColour(sku: string, colour: string): string {
  if (colour !== 'Black') return sku
  return SILVER_TO_BLACK[sku] ?? sku
}

export type ClampStyle = 'clickLock' | 'tBolt'

const TBOLT_MID_CLAMP_SKUS: Record<number, string> = {
  30: 'FR-MIGS-30',
  35: 'FR-MIGS-35',
  40: 'FR-MIGS-40',
}

const TBOLT_END_CLAMP_SKUS: Record<number, string> = {
  30: 'FR-END-30',
  35: 'FR-END-35',
  40: 'FR-END-40',
}

const CLICK_LOCK_STOPPER_SKUS: Record<number, string> = {
  30: 'FR-CL-END-30',
  32: 'FR-CL-END-32',
  35: 'FR-CL-END-35',
  40: 'FR-CL-END-40',
}

export const CLICK_LOCK_MID_CLAMP_SKU = 'FR-MIG-CL-1300'
export const HIDDEN_END_CLAMP_SKU = 'FR-END-HEC'

/** Fast Rack DB codes for third-party anchors stored under the Fast-Rack manufacturer. */
export const ANCHOR_SKU = {
  TALON_XE: 'FR-TALON-XE',
  RT_MINI_BOX_20: 'FR-RT-MINI-BOX-20',
  S5_CLAMPS: 'FR-S-5-CLAMPS',
  PM_BRACKET: 'FR-PM-BRACKET',
  EJOT_8_0_110: 'FR-EJOT-8.0-110',
  S5_RIBBRACKET: 'FR-S-5-RIBBRACKET',
} as const

export const RT_MINI_SCREW_SKU = 'FR-RT-MINI-SCREW-100'

export type AnchorType = (typeof ANCHOR_SKU)[keyof typeof ANCHOR_SKU]

const LEGACY_ANCHOR_SKU: Record<string, AnchorType> = {
  'RT-MINI-BOX-20': ANCHOR_SKU.RT_MINI_BOX_20,
  'S-5-CLAMPS': ANCHOR_SKU.S5_CLAMPS,
  'PM-BRACKET': ANCHOR_SKU.PM_BRACKET,
  'EJOT-8.0-110': ANCHOR_SKU.EJOT_8_0_110,
  'S-5-RIBBRACKET': ANCHOR_SKU.S5_RIBBRACKET,
}

export function normalizeAnchorType(anchor: string): AnchorType {
  return (LEGACY_ANCHOR_SKU[anchor] ?? anchor) as AnchorType
}

export type RoofAnchorGroup = 'asphaltShingle' | 'standingSeam' | 'corrugatedMetal'

export function anchorGroup(anchor: string): RoofAnchorGroup {
  const normalized = normalizeAnchorType(anchor)
  if (normalized === ANCHOR_SKU.TALON_XE || normalized === ANCHOR_SKU.RT_MINI_BOX_20) return 'asphaltShingle'
  if (normalized === ANCHOR_SKU.S5_CLAMPS) return 'standingSeam'
  return 'corrugatedMetal'
}

export interface ScrewSpec {
  sku: string
  qtyPerAnchor: number
}

/**
 * Determines the screw SKU and per-anchor quantity, or null when no
 * separate screw line item is needed (e.g. RT-MINI truss, all metal anchors).
 */
export function selectScrews(
  anchor: string,
  mountType: 'SHEATHING' | 'TRUSS'
): ScrewSpec | null {
  const normalized = normalizeAnchorType(anchor)
  if (normalized === ANCHOR_SKU.TALON_XE) {
    return mountType === 'SHEATHING'
      ? { sku: 'FR-SCREW', qtyPerAnchor: 6 }
      : { sku: 'FR-SCREW-STR', qtyPerAnchor: 2 }
  }
  if (normalized === ANCHOR_SKU.RT_MINI_BOX_20) {
    return mountType === 'SHEATHING' ? { sku: RT_MINI_SCREW_SKU, qtyPerAnchor: 3 } : null
  }
  return null
}

export type PestProtection = 'none' | 'critterGuard' | 'edgeTrim'
export type FenceHeight = '6' | '8'
export type FenceAttachment = 'jhooksCgClips' | 'jhooksEdgeClamps' | 'edgeClamps'

export const SKU = {
  SPLICE: 'FR-SPLICE-C',
  BONDING_JUMPER: 'FR-BJ-8.0-KIT',
  END_CAP: 'FR-ACC-3200-END-CAP',
  FOOT_KIT: 'FR-FOOT-ROS-KIT',
  HIDDEN_END_CLAMP: HIDDEN_END_CLAMP_SKU,
  GROUND_KIT_16: 'FR-MGH-KIT-16',
  GROUND_KIT_20: 'FR-MGH-KIT-20',
  GROUND_LUG: 'FR-BONDLUG',
  FENCE_6: 'FR-FENCE-6',
  FENCE_8: 'FR-FENCE-8',
  JHOOK: 'FR-JHOOK',
  CG_CLIP: 'FR-ACC-3300-CG-CLIP',
  EDGE_CLAMP: 'FR-EDGE-CLAMP',
  EDGE_TRIM: 'FR-EDGE-TRIM',
  CABLE_CLIP: 'FR-ACC-CABLE-CLIP',
} as const

/**
 * Selects the ground kit SKU based on the system's inverter manufacturer.
 * Returns null when no inverter is selected (skip ground kit).
 */
export function selectGroundKitSku(inverterManufacturer: string | null): string | null {
  if (!inverterManufacturer) return null
  const mfr = inverterManufacturer.toLowerCase()
  if (mfr.includes('solaredge') || mfr.includes('enphase')) return SKU.GROUND_KIT_16
  return SKU.GROUND_KIT_20
}

export interface ClampSelection {
  midClampSku: string
  endClampSku: string
  stopperSku: string | null
  error: string | null
}

/**
 * Resolves clamp and stopper SKUs based on clamp style, hidden preference,
 * frame depth, and colour. Returns an error string when the panel's frame
 * depth is incompatible (validation rules V2/V2b/V2c).
 */
export function selectClampSkus(
  clampStyle: ClampStyle,
  hiddenClamp: boolean,
  frameDepth_mm: number,
  colour: string
): ClampSelection {
  if (clampStyle === 'tBolt') {
    return selectTBoltClamps(hiddenClamp, frameDepth_mm, colour)
  }
  return selectClickLockClamps(hiddenClamp, frameDepth_mm, colour)
}

function selectTBoltClamps(hiddenClamp: boolean, frameDepth_mm: number, colour: string): ClampSelection {
  const baseMidSku = TBOLT_MID_CLAMP_SKUS[frameDepth_mm]
  if (!baseMidSku) {
    return {
      midClampSku: '',
      endClampSku: '',
      stopperSku: null,
      error: window.translate?.(MOUNT_FR.tboltClampDepthError, { frameDepth_mm: String(frameDepth_mm) }) ?? `T-Bolt clamps require exact frame depth match (30/35/40 mm). Panel depth ${frameDepth_mm} mm is not supported.`,
    }
  }

  if (hiddenClamp) {
    return {
      midClampSku: resolveColour(baseMidSku, colour),
      endClampSku: HIDDEN_END_CLAMP_SKU,
      stopperSku: null,
      error: null,
    }
  }

  const baseEndSku = TBOLT_END_CLAMP_SKUS[frameDepth_mm]
  if (!baseEndSku) {
    return {
      midClampSku: resolveColour(baseMidSku, colour),
      endClampSku: '',
      stopperSku: null,
      error: window.translate?.(MOUNT_FR.tboltEndClampDepthError, { frameDepth_mm: String(frameDepth_mm) }) ?? `T-Bolt end clamps require exact frame depth match (30/35/40 mm). Panel depth ${frameDepth_mm} mm is not supported.`,
    }
  }

  return {
    midClampSku: resolveColour(baseMidSku, colour),
    endClampSku: resolveColour(baseEndSku, colour),
    stopperSku: null,
    error: null,
  }
}

function selectClickLockClamps(hiddenClamp: boolean, frameDepth_mm: number, colour: string): ClampSelection {
  if (frameDepth_mm < 30 || frameDepth_mm > 40) {
    return {
      midClampSku: '',
      endClampSku: '',
      stopperSku: null,
      error: window.translate?.(MOUNT_FR.clickLockMidDepthError, { frameDepth_mm: String(frameDepth_mm) }) ?? `Click-Lock mid clamps support 30–40 mm frame depth. Panel depth ${frameDepth_mm} mm is outside range.`,
    }
  }

  const midClampSku = resolveColour(CLICK_LOCK_MID_CLAMP_SKU, colour)

  if (hiddenClamp) {
    return { midClampSku, endClampSku: HIDDEN_END_CLAMP_SKU, stopperSku: null, error: null }
  }

  const baseStopperSku = CLICK_LOCK_STOPPER_SKUS[frameDepth_mm]
  if (!baseStopperSku) {
    return {
      midClampSku,
      endClampSku: midClampSku,
      stopperSku: null,
      error: window.translate?.(MOUNT_FR.clickLockStopperDepthError, { frameDepth_mm: String(frameDepth_mm) }) ?? `Click-Lock stoppers require exact frame depth (30/32/35/40 mm). Panel depth ${frameDepth_mm} mm has no matching stopper.`,
    }
  }

  return {
    midClampSku,
    endClampSku: midClampSku,
    stopperSku: resolveColour(baseStopperSku, colour),
    error: null,
  }
}
